import cn from 'classnames'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import type { AnimationItem, LottiePlayer } from 'lottie-web'

import { PlayPauseButton } from '../../play-pause'
import { useIsInternalTesting } from '../../ui-lib/hooks/use-internal-testing'
import styles from './animation.module.scss'

export interface LottieAnimationProps {
  dataSourceUrl: string
  placeholder: React.ReactNode
  altTitle?: string // `altTitle` is a short, brief description of the animation
  altDescription?: string // `altDescription` is a longer, detailed description of the animation
  loop?: boolean
  dropShadow?: boolean
}

export const LottieAnimation: React.FC<LottieAnimationProps> = (props) => {
  const { loop, dataSourceUrl, altTitle, altDescription, placeholder, dropShadow } = props
  const animationContainerRef = useRef<HTMLDivElement>(null)
  const placeholderContainerRef = useRef<HTMLDivElement>(null)
  const animationRef = useRef<AnimationItem | null>(null)
  const [lottie, setLottie] = useState<LottiePlayer | null>(null)
  const [isAnimationReady, setIsAnimationReady] = useState<boolean>(false)
  const [isAnimationPlaying, setIsAnimationPlaying] = useState(false)
  const isInternalTesting = useIsInternalTesting()

  useEffect(() => {
    import('lottie-web').then((Lottie) => setLottie(Lottie.default))
  }, [])

  useEffect(() => {
    if (lottie && animationContainerRef.current) {
      const animation = lottie.loadAnimation({
        container: animationContainerRef.current,
        renderer: 'svg',
        loop,
        autoplay: false,
        path: dataSourceUrl,
        rendererSettings: {
          progressiveLoad: true,
          title: altTitle,
          description: altDescription,
        },
      })

      animationRef.current = animation

      if (!isInternalTesting) {
        animation.addEventListener('DOMLoaded', () => {
          setIsAnimationPlaying(true)
        })
      }

      animation.addEventListener('data_ready', () => {
        // Hide placeholder as soon as data is ready to avoid situation when both
        // placeholder and animation exist simultaneously
        // Set `isAnimationReady` flag to save the React state and keep the layout during the re-rendering cycle
        placeholderContainerRef.current?.classList.add(styles.hidden)
        setIsAnimationReady(true)
      })

      return () => {
        animation.removeEventListener('DOMLoaded')
        animation.removeEventListener('data_ready')
        animation.destroy()
        animationRef.current = null
      }
    }
  }, [lottie, loop, isInternalTesting, dataSourceUrl, altTitle, altDescription])

  // Use react state as source of truth for animation state
  useEffect(() => {
    if (!animationRef.current) {
      return
    }

    if (isAnimationPlaying) {
      animationRef.current.play()
    } else {
      animationRef.current.pause()
    }
  }, [isAnimationPlaying])

  const onPlayPauseClick = useCallback(() => {
    if (!isAnimationReady || !animationRef.current) {
      return
    }

    if (animationRef.current.isPaused) {
      setIsAnimationPlaying(true)
    } else {
      setIsAnimationPlaying(false)
    }
  }, [isAnimationReady])

  const playPauseAriaLabel = isAnimationPlaying ? 'Pause animation' : 'Play animation'
  const playPauseState = !(isAnimationReady && animationRef.current)
    ? 'inactive'
    : isAnimationPlaying
      ? 'playing'
      : 'paused'

  return (
    <>
      <div
        ref={animationContainerRef}
        className={cn(styles.animationContainer, dropShadow && styles.dropShadow)}
        role="img"
      >
        {!isAnimationReady && (
          <div
            ref={placeholderContainerRef}
            className={cn(isAnimationReady && styles.hidden, dropShadow && styles.dropShadowPlaceholder)}
          >
            {placeholder}
          </div>
        )}
      </div>
      <div className={styles.playPauseContainer}>
        <PlayPauseButton
          state={playPauseState}
          onClick={onPlayPauseClick}
          backgroundColor="#FFF"
          ariaLabel={playPauseAriaLabel}
        />
      </div>
    </>
  )
}
